import { MutationCache } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getErrorCode, getErrorMessage } from '@/lib/errors/get-error';
import type { ErrorCode } from '@/lib/errors/codes';
import type { ApiResource } from '@/lib/graphql/resources';
import { redirectToPaywallIfInactive } from './paywall';

interface MutationMeta extends Record<string, unknown> {
  /** Coupe l'invalidation automatique par ressource. */
  noAutoInvalidate?: boolean;
  /** Ressources supplémentaires à invalider (rare : les groupes UI sont
   * choisis pour qu'une mutation ne touche que sa propre ressource). */
  invalidates?: Array<ApiResource>;
  /**
   * Toast de succès — statique, ou fonction du résultat (retourner
   * `undefined` pour le sauter, utile aux chemins conditionnels type MFA).
   */
  successMessage?: string | ((data: unknown) => string | undefined);
  /** Fallback du toast d'erreur pour cette mutation. */
  errorMessage?: string;
  /** Supprime entièrement le toast d'erreur. */
  noErrorToast?: boolean;
  /**
   * Codes que l'ÉCRAN traite lui-même (message dans le champ concerné) : le
   * toast global ferait doublon. Les autres codes restent couverts.
   */
  silentErrorCodes?: Array<ErrorCode>;
}

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: MutationMeta;
  }
}

// Convention portée par hooks.gen.ts : queryKey/mutationKey = [ressource, Op]
// (ressource = groupe UI, cf. lib/graphql/resources.ts).
function invalidateEntity(client: QueryClient, entity: string) {
  void client.invalidateQueries({
    predicate: (query) => query.queryKey[0] === entity,
  });
}

export const mutationCache = new MutationCache({
  onSuccess: (data, _variables, _onMutateResult, mutation, { client }) => {
    const meta = mutation.options.meta;

    const [entity] = mutation.options.mutationKey ?? [];
    if (!meta?.noAutoInvalidate && typeof entity === 'string') {
      invalidateEntity(client, entity);
    }
    for (const extra of meta?.invalidates ?? []) {
      invalidateEntity(client, extra);
    }

    const message =
      typeof meta?.successMessage === 'function'
        ? meta.successMessage(data)
        : meta?.successMessage;
    if (message) toast.success(message);
  },

  onError: (error, _variables, _onMutateResult, mutation) => {
    // Abonnement inactif → paywall (redirection dure) ; sinon toast FR mappé.
    if (redirectToPaywallIfInactive(error)) return;
    const meta = mutation.options.meta;
    if (meta?.noErrorToast) return;
    const code = getErrorCode(error);
    if (code && meta?.silentErrorCodes?.includes(code)) return;
    toast.error(getErrorMessage(error, mutation.options.meta?.errorMessage));
  },
});
